import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { authFetch } from '../services/authFetch';

const AsyncTasksContext = createContext(null);

export function AsyncTasksProvider({ children }) {
  // Track all active background tasks
  const [tasks, setTasks] = useState({});
  const pollIntervalsRef = useRef({});
  const mountedRef = useRef(true);

  // Update task status
  const updateTask = useCallback((taskId, updates) => {
    setTasks(prev => {
      if (!prev[taskId]) return prev;
      return {
        ...prev,
        [taskId]: { ...prev[taskId], ...updates }
      };
    });
  }, []);

  // Remove task from tracking
  const removeTask = useCallback((taskId) => {
    // Stop polling
    if (pollIntervalsRef.current[taskId]) {
      clearInterval(pollIntervalsRef.current[taskId]);
      delete pollIntervalsRef.current[taskId];
    }

    setTasks(prev => {
      const newTasks = { ...prev };
      delete newTasks[taskId];
      return newTasks;
    });

    // Dismiss toast
    toast.dismiss(taskId);
  }, []);

  // Get polling endpoint for task type
  const getPollingEndpoint = (taskId, taskType) => {
    switch (taskType) {
      case 'role_generation':
        return `/api/roles/${taskId}/generation-status`;
      case 'bulk_role_generation':
        return `/api/roles/bulk-status/${taskId}`;
      case 'image_generation':
        return `/api/roles/${taskId}/image-generation-status`;
      case 'deep_analysis':
        return `/api/applications/${taskId}`;
      case 'interview_analysis':
        return `/api/applications/${taskId}`;
      default:
        return null;
    }
  };

  // Check if task is complete based on response
  const isTaskComplete = (taskType, data) => {
    if (!data) return false;
    
    const status = data.generation_status || data.status;
    if (process.env.NODE_ENV === 'development') console.log(`[AsyncTasks] isTaskComplete check - taskType: ${taskType}, status: ${status}`);
    
    switch (taskType) {
      case 'role_generation':
      case 'bulk_role_generation':
        const isComplete = status === 'completed' || status === 'error';
        if (process.env.NODE_ENV === 'development') console.log(`[AsyncTasks] role_generation isComplete: ${isComplete}`);
        return isComplete;
      case 'image_generation':
        return data.status === 'completed' || data.status === 'error' || data.status === 'failed' || data.images_generated === true;
      case 'deep_analysis':
      case 'interview_analysis':
        return data.exercise_deep_analysis_status !== 'processing' && 
               data.interview_deep_analysis_status !== 'processing';
      default:
        return true;
    }
  };

  // Start polling for a task
  const startPollingTask = useCallback((taskId, taskType) => {
    const endpoint = getPollingEndpoint(taskId, taskType);
    if (!endpoint) return;

    // Clear existing interval if any
    if (pollIntervalsRef.current[taskId]) {
      clearInterval(pollIntervalsRef.current[taskId]);
    }

    const poll = async () => {
      if (!mountedRef.current) return;

      try {
        const response = await authFetch(endpoint);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        if (!mountedRef.current) return;

        // Update task with new data
        const progress = data.progress || 0;
        const status = data.generation_status || data.status || 'processing';
        
        updateTask(taskId, { 
          progress, 
          status,
          data 
        });

        // Check if complete
        if (isTaskComplete(taskType, data)) {
          if (process.env.NODE_ENV === 'development') console.log(`[AsyncTasks] Task ${taskId} is complete, stopping polling`);
          
          // Stop polling
          if (pollIntervalsRef.current[taskId]) {
            clearInterval(pollIntervalsRef.current[taskId]);
            delete pollIntervalsRef.current[taskId];
            if (process.env.NODE_ENV === 'development') console.log(`[AsyncTasks] Cleared polling interval for ${taskId}`);
          }

          // Update toast
          if (status === 'error' || status === 'failed') {
            toast.error(data.error_message || data.generation_error || 'Error en el proceso', { id: taskId });
          } else {
            toast.success('Proceso completado', { id: taskId });
          }

          // Update final status and remove task after a delay
          updateTask(taskId, { 
            status: status === 'error' ? 'error' : 'completed',
            completedAt: Date.now()
          });
          
          // Remove task from active tasks after 5 seconds
          setTimeout(() => {
            setTasks(prev => {
              const newTasks = { ...prev };
              delete newTasks[taskId];
              return newTasks;
            });
            if (process.env.NODE_ENV === 'development') console.log(`[AsyncTasks] Removed task ${taskId} from active tasks`);
          }, 5000);
        }
      } catch (err) {
        console.error(`Polling error for task ${taskId}:`, err);
      }
    };

    // Initial poll
    poll();

    // Setup interval (every 2 seconds)
    pollIntervalsRef.current[taskId] = setInterval(poll, 2000);
  }, [updateTask]);

  // Add a new task to track
  const addTask = useCallback((taskId, taskType, metadata = {}) => {
    setTasks(prev => ({
      ...prev,
      [taskId]: {
        id: taskId,
        type: taskType,
        status: 'processing',
        progress: 0,
        startedAt: Date.now(),
        ...metadata
      }
    }));

    // Show toast notification
    toast.loading(metadata.message || 'Procesando...', {
      id: taskId,
      duration: Infinity
    });

    // Start polling for this task
    startPollingTask(taskId, taskType);
  }, [startPollingTask]);

  // Fire and forget - send request and track in background
  const fireAndForget = useCallback(async (endpoint, options = {}) => {
    const {
      method = 'POST',
      body = null,
      taskType = 'generic',
      taskId = null,
      message = 'Procesando...',
      successMessage = 'Completado',
      errorMessage = 'Error',
      onInitiated = null,
      onComplete = null,
      onError = null
    } = options;

    try {
      const response = await authFetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : null
      });

      const responseClone = response.clone();
      let data;
      try {
        data = await response.json();
      } catch (e) {
        const text = await responseClone.text();
        console.error('Parse error:', text);
        throw new Error(errorMessage);
      }

      if (!response.ok) {
        throw new Error(data.detail || errorMessage);
      }

      // Get task ID from response or use provided one
      const actualTaskId = data.id || data.role_id || data.batch_id || taskId || `task_${Date.now()}`;

      // Notify user that process started
      toast.success(message, { duration: 2000 });

      // If this is a background task, start tracking it
      if (taskType !== 'instant' && actualTaskId) {
        addTask(actualTaskId, taskType, { 
          message,
          successMessage,
          errorMessage
        });
      }

      if (onInitiated) {
        onInitiated(data);
      }

      return { success: true, data, taskId: actualTaskId };
    } catch (err) {
      toast.error(err.message || errorMessage);
      
      if (onError) {
        onError(err);
      }

      return { success: false, error: err.message };
    }
  }, [addTask]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
      // Clear all polling intervals
      Object.values(pollIntervalsRef.current).forEach(clearInterval);
      pollIntervalsRef.current = {};
    };
  }, []);

  const value = {
    tasks,
    addTask,
    updateTask,
    removeTask,
    fireAndForget,
    getTasksByType: (type) => Object.values(tasks).filter(t => t.type === type),
    hasActiveTasks: Object.values(tasks).some(t => t.status === 'processing'),
    activeTaskCount: Object.values(tasks).filter(t => t.status === 'processing').length
  };

  return (
    <AsyncTasksContext.Provider value={value}>
      {children}
    </AsyncTasksContext.Provider>
  );
}

export function useAsyncTasks() {
  const context = useContext(AsyncTasksContext);
  if (!context) {
    throw new Error('useAsyncTasks must be used within AsyncTasksProvider');
  }
  return context;
}

export default AsyncTasksContext;
